/**
 * Dashboard Header Component
 *
 * Sticky header with:
 * - SidebarTrigger (replaces hamburger)
 * - Breadcrumb-style page title
 * - Search, theme toggle, and notifications
 */

import React from 'react'
import { useLocation } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Input } from '../ui/Input'
import { Separator } from '../ui/Separator'
import { SidebarTrigger } from '../ui/sidebar'
import { ThemeToggle } from '../ui/ThemeToggle'
import { NotificationCenter } from '../notifications/NotificationCenter'

const getPageTitle = (pathname: string): string => {
  const pathSegments = pathname.split('/').filter(Boolean)

  if (pathSegments.length === 1 && pathSegments[0] === 'dashboard') {
    return 'Dashboard Overview'
  }

  if (pathSegments.length >= 2 && pathSegments[0] === 'dashboard') {
    const section = pathSegments[1]
    switch (section) {
      case 'chatbots':
        return 'My Chatbots'
      case 'analytics':
        return 'Analytics & Insights'
      case 'settings':
        return 'Account Settings'
      case 'training':
        return 'Training Data'
      case 'chat':
        return 'Test Chat'
      case 'conversations':
        return 'Conversations'
      case 'teams':
        return 'Teams'
      case 'agents':
        return 'Agents'
      case 'widget':
        return 'Chat Widget'
      case 'skills':
        return 'Skills'
      case 'leads':
        return 'Leads'
      case 'integrations':
        return 'Integrations'
      case 'plans':
        return 'Plans & Billing'
      case 'developers':
        if (pathSegments[2] === 'api-keys') return 'API Keys'
        if (pathSegments[2] === 'guide') return 'API Guide'
        return 'Developers'
      default:
        return section.charAt(0).toUpperCase() + section.slice(1)
    }
  }

  return 'Dashboard'
}

export const Header: React.FC = () => {
  const location = useLocation()
  const pageTitle = getPageTitle(location.pathname)

  return (
    <header className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-md px-4">
      {/* Sidebar toggle + separator */}
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      {/* Page title */}
      <h1 className="text-sm font-medium text-foreground truncate">
        {pageTitle}
      </h1>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right section: Search + Theme + Notifications */}
      <div className="flex items-center gap-2">
        <div className="hidden md:block w-64">
          <Input
            type="search"
            placeholder="Search..."
            startIcon={<Search className="h-4 w-4" />}
            className="h-8 bg-muted/50 border-transparent text-sm"
          />
        </div>

        <ThemeToggle />
        <NotificationCenter />
      </div>
    </header>
  )
}
